import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, LogOut, Search, UserMinus, UserPlus } from 'lucide-react';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Checkbox } from '@/shared/ui/Checkbox';
import { Avatar } from '@/shared/ui/Avatar';
import { dialogs } from '@/shared/ui/dialogStore';
import { toast } from '@/shared/ui/toastStore';
import { useAuthStore } from '@/entities/session/model/authStore';
import { useUserPhoto } from '@/features/presence/model/presenceHooks';
import { chatApi } from '../api/chatApi';
import { useChatStore } from '../model/chatStore';
import type { ChatContact, ChatConversation, ChatGroupDetail } from '../model/types';

function ContactRow({ c, checked, onToggle }: { c: ChatContact; checked: boolean; onToggle: () => void }) {
  const photo = useUserPhoto(c.userId);
  return (
    <label className="hover:bg-bg-hover flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5">
      <Checkbox checked={checked} onCheckedChange={onToggle} />
      <Avatar name={c.userName} imageBase64={photo} className="h-7 w-7 text-[10px]" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm">{c.userName}</span>
        <span className="text-fg-subtle block truncate text-[11px]">{c.roleName ?? c.userCode}</span>
      </span>
    </label>
  );
}

/** Kişi seçici: aranabilir, çoklu seçim. */
function ContactPicker({ exclude, selected, onChange }: { exclude?: string[]; selected: string[]; onChange: (ids: string[]) => void }) {
  const { t } = useTranslation();
  const contacts = useChatStore((s) => s.contacts);
  const loadContacts = useChatStore((s) => s.loadContacts);
  const [q, setQ] = useState('');
  useEffect(() => {
    void loadContacts();
  }, [loadContacts]);
  const list = useMemo(() => {
    const term = q.trim().toLocaleLowerCase('tr');
    const ex = new Set((exclude ?? []).map((x) => x.toLowerCase()));
    return contacts.filter(
      (c) => !ex.has(c.userId.toLowerCase()) && (!term || `${c.userName} ${c.userCode} ${c.roleName ?? ''}`.toLocaleLowerCase('tr').includes(term)),
    );
  }, [contacts, q, exclude]);
  return (
    <div className="flex min-h-0 flex-col gap-2">
      <div className="relative">
        <Search className="text-fg-subtle pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('chat.searchPeople')} className="pl-8" />
      </div>
      <div className="max-h-72 min-h-24 overflow-y-auto">
        {list.map((c) => (
          <ContactRow
            key={c.userId}
            c={c}
            checked={selected.includes(c.userId)}
            onToggle={() => onChange(selected.includes(c.userId) ? selected.filter((x) => x !== c.userId) : [...selected, c.userId])}
          />
        ))}
        {list.length === 0 ? <p className="text-fg-subtle py-6 text-center text-sm">{t('chat.noPeople')}</p> : null}
      </div>
    </div>
  );
}

export function NewGroupDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (key: string) => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [members, setMembers] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const g = await chatApi.createGroup(name.trim(), members);
      await useChatStore.getState().loadConversations();
      onCreated(`g:${g.messageGroupId}`);
    } catch {
      toast.error(t('chat.groupFailed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('chat.newGroup')}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common:Cancel')}</Button>
          <Button disabled={!name.trim() || members.length === 0 || busy} onClick={() => void create()}>
            {t('chat.createGroup')} {members.length ? `(${members.length})` : ''}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Input autoFocus value={name} maxLength={250} onChange={(e) => setName(e.target.value)} placeholder={t('chat.groupName')} />
        <ContactPicker selected={members} onChange={setMembers} />
      </div>
    </Dialog>
  );
}

export function GroupInfoDialog({ groupId, onClose }: { groupId: string; onClose: () => void; conversation?: ChatConversation }) {
  const { t } = useTranslation();
  const myId = useAuthStore((s) => s.user?.userId ?? '');
  const isSys = useAuthStore((s) => !!s.user?.isSysRol);
  const [group, setGroup] = useState<ChatGroupDetail | null>(null);
  const [name, setName] = useState('');
  const [adding, setAdding] = useState<string[] | null>(null);
  const reload = () =>
    chatApi.group(groupId).then((g) => {
      setGroup(g);
      setName(g.groupName);
    });
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);
  const canManage = !!group && (!group.createdByUserId || group.createdByUserId.toLowerCase() === myId.toLowerCase() || isSys);

  const refreshList = () => void useChatStore.getState().loadConversations();

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('chat.groupInfo')}
      size="sm"
      footer={
        adding ? (
          <>
            <Button variant="secondary" onClick={() => setAdding(null)}>{t('common:Cancel')}</Button>
            <Button
              disabled={adding.length === 0}
              onClick={async () => {
                await chatApi.addMembers(groupId, adding);
                setAdding(null);
                await reload();
                refreshList();
              }}
            >
              {t('chat.addMembers')} ({adding.length})
            </Button>
          </>
        ) : (
          <Button
            variant="secondary"
            onClick={async () => {
              if (!(await dialogs.confirm({ message: t('chat.leaveConfirm'), okText: t('chat.leaveGroup') }))) return;
              await chatApi.removeMember(groupId, myId);
              useChatStore.getState().openConversation(null);
              refreshList();
              onClose();
            }}
          >
            <LogOut className="h-4 w-4" /> {t('chat.leaveGroup')}
          </Button>
        )
      }
    >
      {!group ? null : adding ? (
        <ContactPicker exclude={group.members.map((m) => m.userId)} selected={adding} onChange={setAdding} />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex gap-2">
            <Input value={name} disabled={!canManage} maxLength={250} onChange={(e) => setName(e.target.value)} />
            {canManage && name.trim() && name.trim() !== group.groupName ? (
              <Button
                onClick={async () => {
                  await chatApi.renameGroup(groupId, name.trim());
                  await reload();
                  refreshList();
                }}
              >
                {t('common:Save')}
              </Button>
            ) : null}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-fg-muted text-xs font-semibold uppercase">{t('chat.members', { count: group.members.length })}</span>
            {canManage ? (
              <Button variant="ghost" size="sm" onClick={() => setAdding([])}>
                <UserPlus className="h-4 w-4" /> {t('chat.addMembers')}
              </Button>
            ) : null}
          </div>
          <ul className="max-h-72 overflow-y-auto">
            {group.members.map((m) => (
              <li key={m.userId} className="flex items-center gap-2 px-1 py-1.5">
                <Avatar name={m.userName} className="h-7 w-7 text-[10px]" />
                <span className="min-w-0 flex-1 truncate text-sm">{m.userName}</span>
                {group.createdByUserId?.toLowerCase() === m.userId.toLowerCase() ? (
                  <Crown className="h-3.5 w-3.5 text-amber-500" aria-label={t('chat.groupOwner')} />
                ) : null}
                {canManage && m.userId.toLowerCase() !== myId.toLowerCase() ? (
                  <button
                    type="button"
                    aria-label={t('chat.removeMember')}
                    className="text-fg-subtle hover:text-danger rounded p-1"
                    onClick={async () => {
                      await chatApi.removeMember(groupId, m.userId);
                      await reload();
                      refreshList();
                    }}
                  >
                    <UserMinus className="h-4 w-4" />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Dialog>
  );
}
